<template>
  <div class="product-card" @click="$emit('click')">
    <!-- 卡片顶端细彩条：区分产品类别的优雅边框 -->
    <div class="product-card__stripe" :class="'product-card__stripe--' + specInfo.type"></div>

    <!-- 卡片主体信息区：紧凑专业的排版 -->
    <div class="product-card__content">
      <!-- 头部：小而美的高灵动图标圈 + 类别标识 -->
      <div class="product-card__header">
        <div class="icon-badge" :class="'icon-badge--' + specInfo.type">
          <span class="icon">{{ specInfo.icon }}</span>
        </div>
        <div class="meta">
          <span class="category-tag">{{ specInfo.tag }}</span>
        </div>
      </div>

      <!-- 产品名称：粗体突出 -->
      <h4 class="product-card__name" :title="name">{{ name }}</h4>

      <!-- 核心技术优势（卡片关键增信点） -->
      <ul class="product-card__specs">
        <li v-for="(spec, idx) in specInfo.specs" :key="idx" class="spec-item">
          <span class="check">✓</span>
          <span class="text">{{ spec }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductCard',
  props: {
    img: { type: String, default: '' },
    name: { type: String, required: true }
  },
  computed: {
    // 根据产品名称智能输出专业的非标参数、类别标识和图标配色
    specInfo () {
      const n = this.name || ''
      if (n.includes('喷淋塔') || n.includes('洗涤塔')) {
        return {
          type: 'water',
          icon: '🌀',
          tag: '酸碱废气洗涤处理系统',
          specs: ['高效气液两相逆向吸收', '多层喷淋，填料比表面积大', '极低运行阻力，耐酸碱腐蚀']
        }
      } else if (n.includes('活性炭') || n.includes('碳箱')) {
        return {
          type: 'carbon',
          icon: '🪵',
          tag: 'VOCs有机废气吸附装置',
          specs: ['蜂窝状活性炭高效吸附', '抽屉式碳层，填装更换便捷', '极低系统压损，净化率高']
        }
      } else if (n.includes('除尘器') || n.includes('除尘')) {
        return {
          type: 'dust',
          icon: '🌪️',
          tag: '工业精细粉尘净化系统',
          specs: ['脉冲全自动在线喷吹清灰', '除尘滤芯过滤效率≥99.9%', '防静电/防油防水滤料可选']
        }
      } else if (n.includes('生物滴滤塔') || n.includes('生物')) {
        return {
          type: 'bio',
          icon: '🌱',
          tag: '恶臭废气微生物降解设备',
          specs: ['高活性专属菌种快速挂膜', '纯物理降解，零二次污染', '针对臭气硫化氢去除率佳']
        }
      } else if (n.includes('催化燃烧') || n.includes('CO') || n.includes('RCO')) {
        return {
          type: 'fire',
          icon: '🔥',
          tag: '高浓度有机废气催化热解',
          specs: ['贵金属催化剂低能耗自燃', '板式高效余热回收，极省燃气', '系统全防爆配置，安全无忧']
        }
      } else if (n.includes('过滤器') || n.includes('过滤')) {
        return {
          type: 'filter',
          icon: '🛡️',
          tag: '多级初中效预处理过滤箱',
          specs: ['W型折叠式，容尘量巨大', '干式拦截，维护清理零废水', '双层渐进过滤，保护后置主机']
        }
      } else {
        return {
          type: 'general',
          icon: '🌊',
          tag: '环保废气/粉尘清洗系统',
          specs: ['水帘流速均匀，飞漆拦截好', '水泵自循环系统，节水节电', '不锈钢结构，坚固耐磨抗锈']
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.product-card {
  background: #fff;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-md);
    border-color: var(--color-primary);

    .icon-badge {
      transform: scale(1.05) rotate(8deg);
    }
  }

  /* 顶部细彩条设计 */
  &__stripe {
    height: 4px;
    width: 100%;

    &--water { background: linear-gradient(to right, #0284c7, #0d9488); }
    &--carbon { background: linear-gradient(to right, #1e293b, #475569); }
    &--dust { background: linear-gradient(to right, #115e59, #0d9488); }
    &--bio { background: linear-gradient(to right, #065f46, #10b981); }
    &--fire { background: linear-gradient(to right, #9a3412, #ea580c); }
    &--filter { background: linear-gradient(to right, #3730a3, #4f46e5); }
    &--general { background: linear-gradient(to right, #0f766e, #115e59); }
  }

  /* 紧凑而高档的内容区域 */
  &__content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  /* 头部区域（小图标 + 描述标签） */
  &__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.25rem;

    .icon-badge {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: var(--radius-sm);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.35s cubic-bezier(0.165, 0.84, 0.44, 1);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
      flex-shrink: 0;

      .icon {
        font-size: 1.35rem;
        line-height: 1;
      }

      /* 柔和的亮色浅色调背景，突出图标 */
      &--water { background: #e0f2fe; color: #0284c7; }
      &--carbon { background: #f1f5f9; color: #1e293b; }
      &--dust { background: #ccfbf1; color: #115e59; }
      &--bio { background: #d1fae5; color: #065f46; }
      &--fire { background: #ffedd5; color: #9a3412; }
      &--filter { background: #e0e7ff; color: #3730a3; }
      &--general { background: #f0fdf4; color: #0f766e; }
    }

    .meta {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.15rem;

      .category-tag {
        font-size: 0.725rem;
        color: var(--color-text-light);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }
    }
  }

  /* 产品标题 */
  &__name {
    font-weight: 800;
    font-size: 1.2rem;
    color: var(--color-dark);
    margin: 0 0 1rem 0;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  /* 精细化的指标列表 */
  &__specs {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 1;

    .spec-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      text-align: left;

      .check {
        color: var(--color-primary);
        font-weight: 800;
        font-size: 0.8rem;
        line-height: 1.3;
      }

      .text {
        font-size: 0.825rem;
        color: var(--color-text-muted);
        line-height: 1.4;
        font-weight: 500;
      }
    }
  }

}
</style>
